import { AccessTokenType } from '../models/AccessTokens'
import { Methods, padSlash } from './utils'

export const AuthenticationType = {
    AdminToken: AccessTokenType.JWT,
}

export class Endpoint {
    version: string
    path: string
    authenticationType?: string
    method: string
    api: string
    constructor(
        api: string,
        version: string,
        method: string,
        path: string,
        authenticationType?: string
    ) {
        this.api = padSlash(api)
        this.version = padSlash(version)
        this.path = padSlash(path)
        this.authenticationType = authenticationType
        this.method = method
    }

    toString(): string {
        return this.api + this.version + this.path
    }

    withPathParams(pathParams: { [key: string]: string }): Endpoint {
        let pathWithPathParams = this.path
        Object.keys(pathParams).forEach((name) => {
            pathWithPathParams = pathWithPathParams.replace(
                `{${name}}`,
                pathParams[name]
            )
        })
        return new Endpoint(
            this.api,
            this.version,
            this.method,
            pathWithPathParams,
            this.authenticationType
        )
    }
}

export const apis = {
    internal: 'internal',
    public: 'public',
}

export const versions = {
    v0: 'v0',
}

export const endpoints = {
    internal: {
        v0: {
            // Token
            internalAdminToken: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'internal-admin-token'
            ),
            // Partners
            createPartner: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'partners',
                AuthenticationType.AdminToken
            ),
            editPartner: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'partners/{partnerId}',
                AuthenticationType.AdminToken
            ),
            getPartner: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'partners/{partnerId}',
                AuthenticationType.AdminToken
            ),
            listAllPartners: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'partners',
                AuthenticationType.AdminToken
            ),
            deletePartner: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                'partners/{partnerId}',
                AuthenticationType.AdminToken
            ),
            // App developers
            createAppDeveloperInvite: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'app-developer/invites',
                AuthenticationType.AdminToken
            ),
            getAppDeveloperInvites: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'app-developer/invites',
                AuthenticationType.AdminToken
            ),
            getAppDevelopers: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'app-developer/partner/{partnerId}',
                AuthenticationType.AdminToken
            ),
            createAppDeveloper: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'app-developer',
                AuthenticationType.AdminToken
            ),
            editAppDeveloper: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'app-developer/{appDeveloperId}',
                AuthenticationType.AdminToken
            ),
            getAppDeveloper: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'app-developer/{appDeveloperId}',
                AuthenticationType.AdminToken
            ),
            deleteAppDeveloper: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                'app-developer/{appDeveloperId}',
                AuthenticationType.AdminToken
            ),
            // Apps
            createApp: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'apps',
                AuthenticationType.AdminToken
            ),
            editApp: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'apps/{appId}',
                AuthenticationType.AdminToken
            ),
            getApp: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/{appId}',
                AuthenticationType.AdminToken
            ),
            listAllApps: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps',
                AuthenticationType.AdminToken
            ),
            getAppsByPartnerId: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/partner/{partnerId}',
                AuthenticationType.AdminToken
            ),
            getAppsForPartner: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/partner',
                AuthenticationType.AdminToken
            ),
            deleteApp: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                'apps/{appId}',
                AuthenticationType.AdminToken
            ),
            accessKeySecret: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'apps/access-key-secret'
            ),
            regenerateAccessKeySecret: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/apps/{appId}/regenerate-access-key-secret',
                AuthenticationType.AdminToken
            ),
            updateWebOrigins: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Put,
                '/apps/{appId}/web-origins',
                AuthenticationType.AdminToken
            ),
            getWebOrigins: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/apps/{appId}/web-origins',
                AuthenticationType.AdminToken
            ),
            getAppProduct: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/apps/{appId}/app-product',
                AuthenticationType.AdminToken
            ),
            assignAppProduct: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/apps/{appId}/app-product',
                AuthenticationType.AdminToken
            ),
            invalidateAppProduct: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                '/apps/{appId}/app-product',
                AuthenticationType.AdminToken
            ),
            // Rate limits
            editLimit: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'apps/{appId}/ratelimiting',
                AuthenticationType.AdminToken
            ),
            getLimit: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/{appId}/ratelimiting',
                AuthenticationType.AdminToken
            ),
            // App auth config (ES Connect)
            getAppAuthConfig: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/{appId}/auth-config',
                AuthenticationType.AdminToken
            ),
            getDeveloperAppAuthConfig: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'apps/{appId}/developer-auth-config',
                AuthenticationType.AdminToken
            ),
            patchAppAuthConfig: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'apps/{appId}/auth-config',
                AuthenticationType.AdminToken
            ),
            patchAppAuthConfigStatus: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'apps/{appId}/auth-config/status',
                AuthenticationType.AdminToken
            ),
            postPartnerAppLogo: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'apps/{appId}/logo',
                AuthenticationType.AdminToken
            ),
            putAppScopes: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Put,
                'apps/{appId}/auth-config/scopes',
                AuthenticationType.AdminToken
            ),
            deletePartnerAppLogo: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                'apps/{appId}/logo',
                AuthenticationType.AdminToken
            ),
            // Tiers
            getAllTiers: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'tiers',
                AuthenticationType.AdminToken
            ),
            getTier: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'tiers/{tierId}',
                AuthenticationType.AdminToken
            ),
            createTier: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                'tiers',
                AuthenticationType.AdminToken
            ),
            editTier: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                'tiers/{tierId}',
                AuthenticationType.AdminToken
            ),
            deleteTier: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                'tiers/{tierId}',
                AuthenticationType.AdminToken
            ),
            // Collections
            getCollections: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/collections',
                AuthenticationType.AdminToken
            ),
            getCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            createCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/collections',
                AuthenticationType.AdminToken
            ),
            toggleCollectionStatus: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/collections/toggle',
                AuthenticationType.AdminToken
            ),
            updateCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            deleteCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                '/collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            syncCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/collections/{collectionId}/sync',
                AuthenticationType.AdminToken
            ),
            // Curated collections
            getCuratedCollections: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/curated-collections',
                AuthenticationType.AdminToken
            ),
            getCuratedCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/curated-collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            createCuratedCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/curated-collections',
                AuthenticationType.AdminToken
            ),
            patchCuratedCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/curated-collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            syncCuratedCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/curated-collections/{collectionId}/sync',
                AuthenticationType.AdminToken
            ),
            deleteCuratedCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                '/curated-collections/{collectionId}',
                AuthenticationType.AdminToken
            ),
            // Templates
            getTemplates: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/app-catalog-templates',
                AuthenticationType.AdminToken
            ),
            getTemplate: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/app-catalog-templates/{templateId}',
                AuthenticationType.AdminToken
            ),
            createTemplate: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/app-catalog-templates',
                AuthenticationType.AdminToken
            ),
            patchTemplate: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/app-catalog-templates/{templateId}',
                AuthenticationType.AdminToken
            ),
            toggleTemplateCollection: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/app-catalog-templates/{templateId}/toggle',
                AuthenticationType.AdminToken
            ),
            deleteTemplate: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Delete,
                '/app-catalog-templates/{templateId}',
                AuthenticationType.AdminToken
            ),
            //Products
            getProducts: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                '/product',
                AuthenticationType.AdminToken
            ),
            createProduct: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Post,
                '/product',
                AuthenticationType.AdminToken
            ),
            updateProduct: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Patch,
                '/product/{productId}',
                AuthenticationType.AdminToken
            ),
            getTrackHighlights: new Endpoint(
                apis.internal,
                versions.v0,
                Methods.Get,
                'tracks/{trackId}/highlights',
                AuthenticationType.AdminToken
            ),
        },
    },
    public: {
        v0: {
            getCollections: new Endpoint(
                apis.public,
                versions.v0,
                Methods.Get,
                '/collections'
            ),
        },
    },
}
